import type { IStep } from '@chainlit/react-client';

/**
 * What the model remembers of this conversation, as the server put it on the
 * newest answer (metadata.memory, signals-copilot server/conversation_memory.py).
 *
 * The count is made inside the container, on the very list the model is sent
 * (docker/conversation.py memory_state). Nothing here re-counts it: this only
 * finds the newest one and decides where to draw it.
 */
export interface MemoryState {
  remembered: number;
  max: number;
  keep: number;
  firstQuestionId?: string;
}

export interface LatestMemory {
  state: MemoryState;
  /** Index in `flat` of the step that carries the count. */
  carrierIndex: number;
  flat: IStep[];
}

/** Every step in the order the thread shows them, nested runs included. */
export function flattenSteps(messages: IStep[] | undefined): IStep[] {
  const out: IStep[] = [];
  const walk = (steps: IStep[] | undefined) => {
    for (const step of steps || []) {
      out.push(step);
      if (step.steps?.length) walk(step.steps);
    }
  };
  walk(messages);
  return out;
}

function parse(raw: unknown): MemoryState | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const m = raw as Record<string, unknown>;
  const remembered = Number(m.remembered);
  const max = Number(m.max);
  const keep = Number(m.keep);
  if (![remembered, max, keep].every(Number.isInteger)) return undefined;
  if (max <= 0 || remembered < 0 || remembered > max) return undefined;
  const first = m.first_question_id;
  return {
    remembered,
    max,
    keep,
    firstQuestionId: typeof first === 'string' && first ? first : undefined
  };
}

/** The newest memory state in the thread and the step that carries it. */
export function latestMemory(
  messages: IStep[] | undefined
): LatestMemory | undefined {
  const flat = flattenSteps(messages);
  for (let i = flat.length - 1; i >= 0; i--) {
    const state = parse(
      (flat[i].metadata as Record<string, unknown> | undefined)?.memory
    );
    if (state) return { state, carrierIndex: i, flat };
  }
  return undefined;
}

/**
 * The user message the model's memory starts at, when older questions have
 * dropped out; undefined while it still remembers every question shown.
 *
 * The container names that message when the server told it the ids. Without
 * one (an older server), it is the n-th most recent user message before the
 * answer that carries the count -- not counting a question asked since, which
 * the count does not know about yet.
 */
export function memoryAnchor(
  latest: LatestMemory | undefined
): string | undefined {
  if (!latest || latest.state.remembered <= 0) return undefined;
  const asked = latest.flat
    .slice(0, latest.carrierIndex)
    .filter((s) => s.type === 'user_message');
  if (asked.length <= latest.state.remembered) return undefined;
  const named = latest.state.firstQuestionId;
  if (named) {
    const at = asked.findIndex((s) => s.id === named);
    if (at > 0) return named;
    if (at === 0) return undefined;
  }
  return asked[asked.length - latest.state.remembered]?.id;
}

/** Ids of every step above the anchor: drawn faded, still readable. */
export function stepsAbove(
  messages: IStep[] | undefined,
  anchorId: string | undefined
): Set<string> {
  const above = new Set<string>();
  if (!anchorId) return above;
  for (const step of flattenSteps(messages)) {
    if (step.id === anchorId) return above;
    above.add(step.id);
  }
  return new Set();
}

export const isMemoryFull = (s: MemoryState | undefined) =>
  !!s && s.remembered >= s.max;

/** The pill's words. English always: the system speaks English. */
export function memoryLabel(s: MemoryState): string {
  if (isMemoryFull(s)) return `Memory full · ${s.max} questions`;
  if (s.remembered === 1) return 'Remembers 1 question';
  return `Remembers last ${s.remembered} questions`;
}

/** "your last 4 questions" / "your last question". */
export function rememberedPhrase(s: MemoryState): string {
  return s.remembered === 1
    ? 'your last question'
    : `your last ${s.remembered} questions`;
}

// The new-topic nudge shows at most once per chat. Kept in the browser: it is
// a courtesy, and a private window that forgets it shows it once more at most.
const NUDGE_KEY = 'signals.memoryNudge.';
const shownHere = new Set<string>();

export function nudgeAlreadyShown(threadId: string | undefined): boolean {
  if (!threadId) return false;
  if (shownHere.has(threadId)) return true;
  try {
    return window.localStorage.getItem(NUDGE_KEY + threadId) === '1';
  } catch {
    return false;
  }
}

export function markNudgeShown(threadId: string | undefined): void {
  if (!threadId) return;
  shownHere.add(threadId);
  try {
    window.localStorage.setItem(NUDGE_KEY + threadId, '1');
  } catch {
    // storage blocked: the in-memory set still covers this tab
  }
}
